// ANGULAR
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import {
  Component,
  effect,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { Title } from '@angular/platform-browser';

// API
import {
  IWHBreadcrumbModel,
  WHRouteZeroENUM,
  WHLoginDataService,
  WHTeamDataService,
  WHTeamDOM,
  WHDomEventService,
  WHActiveUserDOM,
  WHPermissionKeyENUM,
  WHIconENUM,
  WHDomEvent,
  WHSafeTranslateService,
  WHBreadcrumbsStore,
} from '@workheld/workheld-shared-lib';

// RxJS 6
import { of, Subscription } from 'rxjs';
import { FormControl } from '@angular/forms';
import { CheckUnsavedComponent } from 'src/app/app-shared/guards/check-unsaved.guard';
import { FormReferenceService } from 'src/app/app-services-helper/form-reference.service';
import { switchMap } from 'rxjs/operators';

export const ROUTE_NAME = {
  OVERVIEW: 'nav.overview.root',
  WORK: 'nav.work.root',
  MASTERDATA: 'nav.masterdata.root',
  HUMANRESOURCES: 'nav.humanresources.root',
  EQUIPMENT: 'nav.equipment.root',
  MATERIAL: 'nav.material.root',
  SHIFT: 'shift.label.shiftModels',
};

@Component({
  selector: 'app-w-h-content-page',
  templateUrl: './w-h-content-page.component.html',
  standalone: false,
})
export class WHContentPageComponent
  implements OnInit, OnDestroy, CheckUnsavedComponent
{
  // VAR
  public permissions: string[];
  public basePath: string = '';
  public routeWhitelist: string[] = [WHRouteZeroENUM.PROFILE];
  private readonly breadcrumbStore = inject(WHBreadcrumbsStore);
  public breadcrumbs = this.breadcrumbStore.breadcrumbs;

  // TEAMS
  public displayFilter: boolean = false;
  public teamFilterWhitelist: string[] = [
    WHRouteZeroENUM.PROJECTS,
    WHRouteZeroENUM.CASES,
    WHRouteZeroENUM.STANDING_ORDER,
    WHRouteZeroENUM.HOME,
    WHRouteZeroENUM.TEAM_PLANNER,
    WHRouteZeroENUM.HR_WORKERS,
    WHRouteZeroENUM.JOB_REQUIREMENTS,
    WHRouteZeroENUM.SHIFT_DEFINITIONS,
    WHRouteZeroENUM.SHIFT_CALENDAR,
    WHRouteZeroENUM.SHIFT_PLANNER,
    WHRouteZeroENUM.EQUIPMENT_PLANNER,
    WHRouteZeroENUM.INFOSCREEN,
    WHRouteZeroENUM.SHIFT_BOOK,
  ];

  public isOpen: boolean = false;
  public selectedTeamDOM: WHTeamDOM = undefined;
  public cssPrefix = 'team-dropdown';
  public teamDOMList = signal<WHTeamDOM[]>([]);
  public filteredTeamDOMList: WHTeamDOM[] = [];
  // SEARCH CONTROL
  public searchCtrl: FormControl = new FormControl();

  // SUBSCRIPTION
  private subscription: Subscription = new Subscription();

  // STATE
  private activeUser: WHActiveUserDOM = null;
  canRemoveFilter = signal(false);

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private loginDataService: WHLoginDataService,
    private teamDataService: WHTeamDataService,
    private domEventService: WHDomEventService,
    private formReferenceService: FormReferenceService,
    private safeTranslateService: WHSafeTranslateService,
    private titleService: Title,
  ) {
    // page title and team filter follow the breadcrumbs
    effect(() => {
      const breadcrumbs = this.breadcrumbs();
      this.showTeamFilter(breadcrumbs);
      this.titleService.setTitle(this.buildTitle(breadcrumbs));
    });
  }

  checkUnsaved() {
    return this.formReferenceService.unsavedChanges$.pipe(
      switchMap((isDirty) => {
        if (isDirty) {
          const dialog = this.formReferenceService.createDialog();
          return dialog.pipe((discard) => {
            return discard;
          });
        } else {
          return of(true);
        }
      }),
    );
  }

  public onClickOutside() {
    this.isOpen = false;
  }

  public selectionChange(teamDOM: WHTeamDOM) {
    this.selectedTeamDOM = teamDOM;
    this.domEventService.domEvents$.next({
      type: 'teamFilterChange',
      payload: teamDOM,
    });
    this.isOpen = false;
  }

  ngOnInit(): void {
    this.formReferenceService.unsavedChanges = false;
    // TEAMS
    this.subscription.add(
      this.teamDataService.teams$.subscribe((teams: WHTeamDOM[]) => {
        this.teamDOMList.set(
          [...teams].sort((a: WHTeamDOM, b: WHTeamDOM) => {
            if (a.name.toLowerCase() < b.name.toLowerCase()) {
              return -1;
            }
            if (a.name.toLowerCase() > b.name.toLowerCase()) {
              return 1;
            }
            return 0;
          }),
        );
        this.filteredTeamDOMList = this.teamDOMList();

        if (this.teamDOMList()?.length == 1) {
          this.selectedTeamDOM = this.teamDOMList()[0];
        }
      }),
    );

    this.subscription.add(
      this.loginDataService.permissions$.subscribe((permissions: string[]) => {
        this.permissions = permissions;
      }),
    );

    this.subscription.add(
      this.domEventService.domEvents$.subscribe((domEvent: WHDomEvent) => {
        if (
          domEvent &&
          (domEvent.type === 'teamFilterChange' ||
            domEvent.type === 'teamFilterRemoved')
        ) {
          this.selectedTeamDOM = domEvent.payload;
        }
      }),
    );

    this.subscription.add(
      this.loginDataService.activeUserDOM$.subscribe(
        (user: WHActiveUserDOM) => {
          this.activeUser = user;

          this.canRemoveFilter.set(
            this.activeUser?.team ? !this.activeUser.team.subcontractor : true,
          );
        },
      ),
    );

    this.subscription.add(
      this.router.events.subscribe((event: any) => {
        if (event.routerEvent && event.routerEvent instanceof NavigationEnd) {
          this.updateBasePath(event.routerEvent.url);
        }
      }),
    );

    this.subscription.add(
      this.activatedRoute.url.subscribe((urlSegment) => {
        if (urlSegment[0]?.path) {
          this.basePath = urlSegment[0].path;
        }
      }),
    );
  }

  private updateBasePath(url: string): void {
    const first = url
      .split('/')
      .map((element) => element.split('?')[0])
      .find((element) => !!element);
    if (first) {
      this.basePath = first;
    }
  }

  private buildTitle(breadcrumbs: IWHBreadcrumbModel[]): string {
    const names = breadcrumbs.map((b) =>
      b.nameI18nKey?.includes('.')
        ? this.safeTranslateService.instant(b.nameI18nKey)
        : b.nameI18nKey,
    );
    return (
      'Workheld Flow' + (names.length > 0 ? ' | ' + names.join(' | ') : '')
    );
  }

  onSearchTerm(value: string) {
    if (!value || value.length === 0) {
      this.filteredTeamDOMList = this.teamDOMList();
    } else {
      this.filteredTeamDOMList = this.teamDOMList()?.filter(
        (teamDOM: WHTeamDOM) => {
          return teamDOM.name.toLowerCase().includes(value.toLowerCase());
        },
      );
    }
  }
  public showTeamFilter(flowCrumbs: IWHBreadcrumbModel[]) {
    if (flowCrumbs[1] && flowCrumbs.length === 2) {
      if (this.teamFilterWhitelist.includes(flowCrumbs[1].urlSegmentPath)) {
        this.displayFilter = true;
      } else {
        this.displayFilter = false;
      }
    } else {
      this.displayFilter = false;
    }
  }

  public get routeWhitelisted() {
    return this.routeWhitelist.includes(this.basePath);
  }

  public get coordinatorAccess() {
    return this.permissions.includes(
      WHPermissionKeyENUM.FLOW_COORDINATOR_ACCESS,
    );
  }

  removeFilter() {
    if (!this.canRemoveFilter()) return;

    this.selectedTeamDOM = null;
    this.domEventService.domEvents$.next({
      type: 'teamFilterChange',
      payload: this.selectedTeamDOM,
    });
  }

  public get filterIcon(): string {
    return WHIconENUM.FilterIcon as string;
  }

  public get cancelIcon(): string {
    return WHIconENUM.CancelIcon as string;
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
